import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import plusIcon from '../../assets/icons/plus.svg';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import CategoryFilters from './CategoryFilters';
import Rows from '../../components/ui/Rows';
import CircularIconButton from '../../components/ui/CircularIconButton';
import EffectPill from './EffectPill';
import Toggle from '../../components/ui/Toggle';
import CustomSnackbar from '../../components/ui/Snackbar';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CreationModal from '../../components/ui/CreationModal';
import { categoryAPI, categoryDetailAPI, caisseAPI } from '../../services/api';
import { isWithdrawalCategory } from '../../utils/categoryUtils';

const Categories = () => {
  const { user } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
  const [statusDialog, setStatusDialog] = useState({ open: false, category: null, action: null, formData: null });
  const [notificationDialog, setNotificationDialog] = useState({ open: false, category: null, action: null });
  const [createModal, setCreateModal] = useState({ open: false, errors: {} });
  const [editModal, setEditModal] = useState({ open: false, category: null, errors: {} });
  const [editConfirmDialog, setEditConfirmDialog] = useState({ open: false, changes: null });
  const [assignCategoryDialog, setAssignCategoryDialog] = useState({ 
    open: false, 
    category: null, 
    formData: null,
    response: null 
  });
  const [physiqueCaisses, setPhysiqueCaisses] = useState([]);
  const [mobileCaisses, setMobileCaisses] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 20,
    total: 0
  });
  
  const [filters, setFilters] = useState({
    search: '',
    caisseId: '',
    caisseFactor: '',
    coffreFactor: '',
    active: '',
    notifyManager: '',
    requiresPointage: ''
  });
  
  const [sortBy, setSortBy] = useState('category.label');
  const [sortDirection, setSortDirection] = useState('ASC');

  const contentContainerSx = {
    width: '100%',
    margin: '0 auto',
    paddingLeft: { xs: '26px', sm: '42px' },
    paddingRight: { xs: '26px', sm: '42px' }
  };

  const stickyOuterSx = { ...contentContainerSx };
  
  // Header height is approximately 76px, so sticky header starts below it
  const HEADER_HEIGHT = 76;


  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const caisseId = filters.caisseId;
      const caisseIds = caisseId === 'ALL_MOBILE' ? mobileCaisses.map(c => c.id)
        : caisseId === 'ALL_PHYSIQUE' ? physiqueCaisses.map(c => c.id)
          : null;
      const params = {
        page: pagination.page,
        size: pagination.size,
        sortBy: sortBy,
        sortDirection: sortDirection,
        ...(filters.search && { searchTerm: filters.search }),
        ...(caisseIds?.length ? { caisseIds } : caisseId && caisseId !== 'ALL_MOBILE' && caisseId !== 'ALL_PHYSIQUE' && { caisseId: Number(caisseId) }),
        ...(filters.caisseFactor && filters.caisseFactor !== '' && { caisseFactor: Number(filters.caisseFactor) }),
        ...(filters.coffreFactor && filters.coffreFactor !== '' && { coffreFactor: Number(filters.coffreFactor) }),
        ...(filters.active && filters.active !== '' && { active: filters.active === 'true' }),
        ...(filters.notifyManager && filters.notifyManager !== '' && { notifyManager: filters.notifyManager === 'true' }),
        ...(filters.requiresPointage && filters.requiresPointage !== '' && filters.requiresPointage !== 'client' && filters.requiresPointage !== 'comptabilite' && { requiresPointage: filters.requiresPointage === 'true' }),
        ...(filters.requiresPointage === 'client' && { requiresPointageClient: true }),
        ...(filters.requiresPointage === 'comptabilite' && { requiresPointageComptabilite: true })
      };
      
      const response = await categoryDetailAPI.getAll(params);
      const responseData = response.data;
      const categories = responseData?.content || [];
      const totalElements = responseData?.totalElements || 0;

      setCategories(categories);
      setPagination(prev => ({
        ...prev,
        total: totalElements
      }));
      
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Erreur lors du chargement des catégories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, filters, sortBy, sortDirection, mobileCaisses, physiqueCaisses]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSort = (field, direction) => {
    const apiSortField = field === 'label' ? 'category.label' : field;
    setSortBy(apiSortField);
    setSortDirection(direction);
    setPagination(prev => ({ ...prev, page: 0 }));
  };


  useEffect(() => {
    const loadCaisses = async () => {
      try {
        const response = await caisseAPI.getForFilters();
        const allCaisses = Array.isArray(response.data) ? response.data : [];
        
        const physiques = allCaisses
          .filter(c => c.type === 'PHYSIQUE')
          .sort((a, b) => a.name.localeCompare(b.name));
        const mobiles = allCaisses
          .filter(c => c.type === 'MOBILE')
          .sort((a, b) => a.name.localeCompare(b.name));
        
        setPhysiqueCaisses(physiques);
        setMobileCaisses(mobiles);
      } catch (error) {
        console.error('Error loading caisses:', error);
      }
    };
    loadCaisses();
  }, []);

  const handleFiltersChange = useCallback((newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const handleResetFilters = useCallback(() => {
    setFilters({
      search: '',
      caisseId: '',
      caisseFactor: '',
      coffreFactor: '',
      active: '',
      notifyManager: '',
      requiresPointage: ''
    });
    setPagination(prev => ({ ...prev, page: 0 }));
  }, []);

  const getPaginatedData = useCallback(() => {
    return categories;
  }, [categories]);
  const getCategoryFields = () => {
    const caisseOptions = [];
    
    if (mobileCaisses.length > 0) {
      caisseOptions.push({
        value: 'ALL_MOBILE',
        label: 'Toutes les caisses mobiles',
        isSpecial: true
      });
    }
    
    if (physiqueCaisses.length > 0) {
      caisseOptions.push({
        value: 'ALL_PHYSIQUE',
        label: 'Toutes les caisses physiques',
        isSpecial: true
      });
    }
    
    physiqueCaisses.forEach(caisse => {
      caisseOptions.push({
        value: caisse.id,
        label: caisse.name
      });
    });
    mobileCaisses.forEach(caisse => {
      caisseOptions.push({
        value: caisse.id,
        label: caisse.name
      });
    });
    
    return [
      {
        name: 'label',
        type: 'text',
        placeholder: 'Libellé de la catégorie',
        required: true,
        validation: (value) => {
          if (value && value.trim().toLowerCase() === 'sortie coffre') {
            return 'Impossible de créer cette catégorie car elle est créée automatiquement lors de la création d\'une caisse';
          }
          return null;
        }
      },
      {
        name: 'caisseFactor',
        type: 'select',
        placeholder: 'Effet Caisse',
        required: true,
        options: [
          { value: 1, label: 'Augmente (+)' },
          { value: 0, label: 'Aucun effet (0)' },
          { value: -1, label: 'Diminue (-)' }
        ]
      },
      {
        name: 'coffreFactor',
        type: 'select',
        placeholder: 'Effet Coffre',
        required: true,
        options: [
          { value: 1, label: 'Augmente (+)' },
          { value: 0, label: 'Aucun effet (0)' },
          { value: -1, label: 'Diminue (-)' }
        ]
      },
      {
        name: 'caisseIds',
        type: 'multiselect',
        placeholder: 'Caisse',
        required: true,
        options: caisseOptions,
        mobileCaisses: mobileCaisses,
        physiqueCaisses: physiqueCaisses,
        transform: (selectedValues) => {
          if (!Array.isArray(selectedValues)) return [];
          
          const hasAllMobile = selectedValues.includes('ALL_MOBILE');
          const hasAllPhysique = selectedValues.includes('ALL_PHYSIQUE');
          
          if (hasAllMobile && hasAllPhysique) {
            const lastSpecialIndex = Math.max(
              selectedValues.lastIndexOf('ALL_MOBILE'),
              selectedValues.lastIndexOf('ALL_PHYSIQUE')
            );
            const lastSpecial = selectedValues[lastSpecialIndex];
            
            if (lastSpecial === 'ALL_MOBILE') {
              const mobileIds = mobileCaisses.map(c => c.id);
              const physiqueIds = physiqueCaisses.map(c => c.id);
              const regularIds = selectedValues.filter(
                v => v !== 'ALL_MOBILE' && v !== 'ALL_PHYSIQUE' &&
                !physiqueIds.includes(v) && !mobileIds.includes(v)
              );
              return [...regularIds, ...mobileIds];
            } else {
              const mobileIds = mobileCaisses.map(c => c.id);
              const physiqueIds = physiqueCaisses.map(c => c.id);
              const regularIds = selectedValues.filter(
                v => v !== 'ALL_MOBILE' && v !== 'ALL_PHYSIQUE' &&
                !physiqueIds.includes(v) && !mobileIds.includes(v)
              );
              return [...regularIds, ...physiqueIds];
            }
          } else if (hasAllMobile) {
            const mobileIds = mobileCaisses.map(c => c.id);
            const regularIds = selectedValues.filter(
              v => v !== 'ALL_MOBILE' && !mobileIds.includes(v)
            );
            return [...regularIds, ...mobileIds];
          } else if (hasAllPhysique) {
            const physiqueIds = physiqueCaisses.map(c => c.id);
            const regularIds = selectedValues.filter(
              v => v !== 'ALL_PHYSIQUE' && !physiqueIds.includes(v)
            );
            return [...regularIds, ...physiqueIds];
          } else {
            return selectedValues.filter(
              v => v !== 'ALL_MOBILE' && v !== 'ALL_PHYSIQUE'
            );
          }
        }
      },
      {
        name: 'requiresPointageClient',
        type: 'select',
        placeholder: 'Pointage Client',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      },
      {
        name: 'requiresPointageComptabilite',
        type: 'select',
        placeholder: 'Pointage Comptabilité',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      },
      {
        name: 'notifyManager',
        type: 'select',
        placeholder: 'Notifications Manager',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      }
    ];
  };

  const editCategoryFields = [
    {
      name: 'label',
      type: 'text',
      placeholder: 'Libellé de la catégorie',
      required: true
    },
    {
      name: 'caisseFactor',
      type: 'select',
      placeholder: 'Effet Caisse',
      required: true,
      options: [
        { value: 1, label: 'Augmente (+)' },
        { value: 0, label: 'Aucun effet (0)' },
        { value: -1, label: 'Diminue (-)' }
      ]
    },
    {
      name: 'coffreFactor',
      type: 'select',
      placeholder: 'Effet Coffre',
      required: true,
      options: [
        { value: 1, label: 'Augmente (+)' },
        { value: 0, label: 'Aucun effet (0)' },
        { value: -1, label: 'Diminue (-)' }
      ]
    },
    {
      name: 'notifyManager',
      type: 'select',
      placeholder: 'Notifications Manager',
      required: true,
      options: [
        { value: true, label: 'Activé' },
        { value: false, label: 'Désactivé' }
      ]
    },
      {
        name: 'requiresPointageClient',
        type: 'select',
        placeholder: 'Nécessite Pointage Client',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      },
      {
        name: 'requiresPointageComptabilite',
        type: 'select',
        placeholder: 'Nécessite Pointage Comptabilité',
        required: true,
        options: [
          { value: true, label: 'Activé' },
          { value: false, label: 'Désactivé' }
        ]
      }
  ];

  const handleCreateCategory = () => {
    setCreateModal({ open: true, errors: {} });
  };

  const handleSubmitEditCategory = async (formData) => {
    if (!editModal.category) return;
    const originalData = {
      label: editModal.category.category?.label || editModal.category.label,
      caisseFactor: editModal.category.caisseFactor,
      coffreFactor: editModal.category.coffreFactor,
      notifyManager: editModal.category.notifyManager,
        requiresPointageClient: editModal.category.requiresPointageClient !== undefined ? editModal.category.requiresPointageClient : false,
        requiresPointageComptabilite: editModal.category.requiresPointageComptabilite !== undefined ? editModal.category.requiresPointageComptabilite : false,
      active: editModal.category.active
    };

    const hasChanges = Object.keys(formData).some(key => {
      return formData[key] !== originalData[key];
    });

    if (!hasChanges) {
      setEditModal({ open: false, category: null, errors: {} });
      return;
    }
      const onlyActiveChanged = Object.keys(formData).every(key => {
        if (key === 'active') {
          return formData[key] !== originalData[key];
        }
      return formData[key] === originalData[key];
    });

    if (onlyActiveChanged) {
      const action = formData.active ? 'activate' : 'deactivate';
      setStatusDialog({ open: true, category: editModal.category, action, formData });
      return;
    }

    setEditConfirmDialog({ open: true, changes: formData });
  };
  const confirmEditChanges = async () => {
    if (!editConfirmDialog.changes) return;

    setActionLoading(true);
    try {
      const categoryData = {
        label: editConfirmDialog.changes.label,
        caisseFactor: editConfirmDialog.changes.caisseFactor,
        coffreFactor: editConfirmDialog.changes.coffreFactor,
        notifyManager: editConfirmDialog.changes.notifyManager,
        requiresPointageClient: editConfirmDialog.changes.requiresPointageClient,
        requiresPointageComptabilite: editConfirmDialog.changes.requiresPointageComptabilite,
        active: editConfirmDialog.changes.active
      };

      await categoryDetailAPI.update(editModal.category.id, categoryData);
      setSnackbar({ open: true, message: 'Catégorie modifiée avec succès', severity: 'success' });
      setEditModal({ open: false, category: null, errors: {} });
      setEditConfirmDialog({ open: false, changes: null });
      await fetchCategories();
    } catch (err) {
      console.error('Error updating category:', err);
      if (err.response?.data?.errors) {
        setEditModal(prev => ({ ...prev, errors: err.response.data.errors }));
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la modification de la catégorie', severity: 'error' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitCreateCategory = async (formData) => {
    setActionLoading(true);
    try {
      let categoryResponse;
      try {
        categoryResponse = await categoryAPI.create({ 
          label: formData.label,
          caisseIds: formData.caisseIds 
        });
      } catch (error) {
        if (error.response?.status === 200 && error.response.data?.categoryExists) {
          const responseData = error.response.data;
          
          if (!responseData.success) {
            setCreateModal(prev => ({
              ...prev,
              errors: {
                label: responseData.message || 'Cette catégorie existe déjà pour les caisses sélectionnées'
              }
            }));
            setActionLoading(false);
            return;
          }
          
          setAssignCategoryDialog({
            open: true,
            category: responseData.category,
            formData: formData,
            response: responseData
          });
          setActionLoading(false);
          return;
        } else if (error.response?.status === 409) {
          const responseData = error.response.data;
          if (responseData?.categoryExists && !responseData.success) {
            setCreateModal(prev => ({
              ...prev,
              errors: {
                label: responseData.message || 'Cette catégorie existe déjà pour les caisses sélectionnées'
              }
            }));
          } else {
            setCreateModal(prev => ({
              ...prev,
              errors: {
                label: 'Cette catégorie existe déjà'
              }
            }));
          }
          setActionLoading(false);
          return;
        } else if (error.response?.status === 400) {
          setCreateModal(prev => ({
            ...prev,
            errors: {
              label: 'Cette catégorie existe déjà'
            }
          }));
          setActionLoading(false);
          return;
        }
        throw error;
      }
      
      if (categoryResponse.status === 200 && categoryResponse.data?.categoryExists) {
        const responseData = categoryResponse.data;
        
        if (!responseData.success) {
          setCreateModal(prev => ({
            ...prev,
            errors: {
              label: responseData.message || 'Cette catégorie existe déjà pour les caisses sélectionnées'
            }
          }));
          setActionLoading(false);
          return;
        }
        
        setAssignCategoryDialog({
          open: true,
          category: responseData.category,
          formData: formData,
          response: responseData
        });
        setActionLoading(false);
        return;
      }
      
      const category = categoryResponse.data;
      if (!category || !category.id) {
        throw new Error('Failed to create category');
      }
      
      await proceedWithCategoryAssignment(category, formData);
    } catch (error) {
      console.error('Error creating category:', error);
      setSnackbar({ 
        open: true, 
        message: `Erreur lors de la création de la catégorie: ${error.response?.data?.message || error.message}`, 
        severity: 'error' 
      });
      setActionLoading(false);
    }
  };

  const proceedWithCategoryAssignment = async (category, formData) => {
    try {
      const categoryDetailData = {
        categoryId: category.id,
        label: formData.label,
        caisseIds: formData.caisseIds,
        caisseFactor: formData.caisseFactor,
        coffreFactor: formData.coffreFactor,
        notifyManager: formData.notifyManager,
        requiresPointageClient: formData.requiresPointageClient,
        requiresPointageComptabilite: formData.requiresPointageComptabilite
      };

      const result = await categoryDetailAPI.createBulk(categoryDetailData);
      
      // Parse response data if it's a string, with error handling
      let responseData;
      try {
        if (typeof result.data === 'string') {
          responseData = JSON.parse(result.data);
        } else {
          responseData = result.data;
        }
      } catch (parseError) {
        // If parsing fails, use the number of caisseIds from formData as the count
        // This is the most reliable source since it's what we actually sent to create
        const caisseIdsCount = formData.caisseIds?.length || 0;
        
        if (caisseIdsCount > 0) {
          // Check if response indicates success (even if truncated)
          const indicatesSuccess = result.data && (
            typeof result.data === 'string' 
              ? (result.data.includes('"success":true') || result.data.includes('"success": true'))
              : result.data.success === true
          );
          
          if (indicatesSuccess || result.status === 200) {
            setSnackbar({ 
              open: true, 
              message: `${caisseIdsCount} catégorie(s) créée(s) avec succès`, 
              severity: 'success' 
            });
            setCreateModal({ open: false, errors: {} });
            await fetchCategories();
            setActionLoading(false);
            return;
          }
        }
        
        // If we can't determine success, throw error (will be caught by outer catch)
        throw new Error('Failed to parse response from server');
      }
      
      if (responseData && responseData.success !== false) {
        // Use createdCount if available, otherwise fall back to createdDetails array length
        const createdCount = responseData.createdCount ?? (responseData.createdDetails?.length ?? 0);
        const errors = responseData.errors || [];
        
        if (errors.length > 0) {
          setSnackbar({ 
            open: true, 
            message: `${createdCount} catégorie(s) créée(s), ${errors.length} erreur(s): ${errors.join('; ')}`, 
            severity: createdCount > 0 ? 'warning' : 'error'
          });
        } else {
          setSnackbar({ 
            open: true, 
            message: `${createdCount} catégorie(s) créée(s) avec succès`, 
            severity: 'success' 
          });
        }
        
        setCreateModal({ open: false, errors: {} });
        await fetchCategories();
        setActionLoading(false);
      } else {
        const errorMsg = responseData?.error || 'Bulk creation failed';
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Error assigning category:', err);
      
      // If it's a 409 conflict but categories were created, try to show success
      if (err.response?.status === 409) {
        // Check if the error response contains createdCount
        let errorData;
        try {
          errorData = typeof err.response?.data === 'string' 
            ? JSON.parse(err.response.data) 
            : err.response?.data;
        } catch (e) {
          // If error response is also malformed, try regex extraction
          if (typeof err.response?.data === 'string') {
            const countMatch = err.response.data.match(/"createdCount"\s*:\s*(\d+)/);
            if (countMatch) {
              const createdCount = parseInt(countMatch[1], 10);
              setSnackbar({ 
                open: true, 
                message: `${createdCount} catégorie(s) créée(s) avec succès`, 
                severity: 'success' 
              });
              setCreateModal({ open: false, errors: {} });
              await fetchCategories();
              setActionLoading(false);
              return;
            }
          }
        }
        
        // If we have errorData with createdCount, use it
        if (errorData?.createdCount !== undefined) {
          const createdCount = errorData.createdCount ?? (errorData.createdDetails?.length ?? 0);
          setSnackbar({ 
            open: true, 
            message: `${createdCount} catégorie(s) créée(s) avec succès`, 
            severity: 'success' 
          });
          setCreateModal({ open: false, errors: {} });
          await fetchCategories();
          setActionLoading(false);
          return;
        }
      }
      
      setSnackbar({ open: true, message: 'Erreur lors de l\'assignation de la catégorie', severity: 'error' });
      setActionLoading(false);
    }
  };

  const handleConfirmAssignCategory = async () => {
    setActionLoading(true);
    const { category, formData, response } = assignCategoryDialog;
    const caisseIdsToAssign = response.selectedCaissesWithoutCategory?.map(c => c.id) || formData.caisseIds;
    
    try {
      
      // Use existing category attributes if available, otherwise use form data
      // This maintains consistency - all caisses with the same category have the same attributes
      const categoryDetailData = {
        categoryId: category.id,
        caisseIds: caisseIdsToAssign,
        caisseFactor: response.existingCaisseFactor !== undefined ? response.existingCaisseFactor : formData.caisseFactor,
        coffreFactor: response.existingCoffreFactor !== undefined ? response.existingCoffreFactor : formData.coffreFactor,
        notifyManager: response.existingNotifyManager !== undefined ? response.existingNotifyManager : formData.notifyManager,
        requiresPointageClient: response.existingRequiresPointageClient !== undefined ? response.existingRequiresPointageClient : formData.requiresPointageClient,
        requiresPointageComptabilite: response.existingRequiresPointageComptabilite !== undefined ? response.existingRequiresPointageComptabilite : formData.requiresPointageComptabilite
      };

      const result = await categoryDetailAPI.createBulk(categoryDetailData);
      
      // Parse response data if it's a string, with error handling
      let responseData;
      try {
        if (typeof result.data === 'string') {
          responseData = JSON.parse(result.data);
        } else {
          responseData = result.data;
        }
      } catch (parseError) {
        // If parsing fails, use the number of caisseIds as fallback
        const caisseIdsCount = caisseIdsToAssign?.length || 0;
        if (caisseIdsCount > 0) {
          setSnackbar({ 
            open: true, 
            message: `${caisseIdsCount} catégorie(s) assignée(s) avec succès`, 
            severity: 'success' 
          });
          setAssignCategoryDialog({ open: false, category: null, formData: null, response: null });
          setCreateModal({ open: false, errors: {} });
          await fetchCategories();
          setActionLoading(false);
          return;
        }
        throw new Error('Failed to parse response from server');
      }
      
      // Check if response indicates success
      // Backend returns success: true when successful, or we check for createdCount/createdDetails
      const isSuccess = responseData && (
        responseData.success === true || 
        (responseData.success !== false && (responseData.createdCount !== undefined || responseData.createdDetails !== undefined)) ||
        (responseData.createdCount !== undefined && responseData.createdCount > 0) ||
        (responseData.createdDetails !== undefined && responseData.createdDetails.length > 0)
      );
      
      if (isSuccess) {
        try {
          // Use createdCount if available, otherwise fall back to createdDetails array length or caisseIds count
          const createdCount = responseData.createdCount ?? (responseData.createdDetails?.length ?? (caisseIdsToAssign?.length ?? 0));
          const errors = responseData.errors || [];
          
          if (errors.length > 0) {
            setSnackbar({ 
              open: true, 
              message: `${createdCount} catégorie(s) assignée(s), ${errors.length} erreur(s): ${errors.join('; ')}`, 
              severity: createdCount > 0 ? 'warning' : 'error'
            });
          } else {
            setSnackbar({ 
              open: true, 
              message: `${createdCount} catégorie(s) assignée(s) avec succès`, 
              severity: 'success' 
            });
          }
          
          setAssignCategoryDialog({ open: false, category: null, formData: null, response: null });
          setCreateModal({ open: false, errors: {} });
          await fetchCategories();
        } catch (innerErr) {
          // If fetchCategories fails, still show success if we have createdCount
          const createdCount = responseData.createdCount ?? (responseData.createdDetails?.length ?? (caisseIdsToAssign?.length ?? 0));
          if (createdCount > 0) {
            setSnackbar({ 
              open: true, 
              message: `${createdCount} catégorie(s) assignée(s) avec succès`, 
              severity: 'success' 
            });
            setAssignCategoryDialog({ open: false, category: null, formData: null, response: null });
            setCreateModal({ open: false, errors: {} });
          } else {
            throw innerErr;
          }
        } finally {
          setActionLoading(false);
        }
      } else {
        // Only throw error if we're certain it failed
        const errorMsg = responseData?.error || 'Bulk assignment failed';
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Error assigning category:', err);
      
      // Check if the error response actually contains success data
      let errorData;
      try {
        errorData = typeof err.response?.data === 'string' 
          ? JSON.parse(err.response.data) 
          : err.response?.data;
      } catch (e) {
        // If error response is also malformed, try regex extraction
        if (typeof err.response?.data === 'string') {
          const countMatch = err.response.data.match(/"createdCount"\s*:\s*(\d+)/);
          if (countMatch) {
            const createdCount = parseInt(countMatch[1], 10);
            if (createdCount > 0) {
              setSnackbar({ 
                open: true, 
                message: `${createdCount} catégorie(s) assignée(s) avec succès`, 
                severity: 'success' 
              });
              setAssignCategoryDialog({ open: false, category: null, formData: null, response: null });
              setCreateModal({ open: false, errors: {} });
              await fetchCategories();
              setActionLoading(false);
              return;
            }
          }
        }
      }
      
      // If error response has createdCount or createdDetails, it might have succeeded
      if (errorData && (errorData.createdCount !== undefined || errorData.createdDetails !== undefined)) {
        const createdCount = errorData.createdCount ?? (errorData.createdDetails?.length ?? (caisseIdsToAssign?.length ?? 0));
        if (createdCount > 0) {
          setSnackbar({ 
            open: true, 
            message: `${createdCount} catégorie(s) assignée(s) avec succès`, 
            severity: 'success' 
          });
          setAssignCategoryDialog({ open: false, category: null, formData: null, response: null });
          setCreateModal({ open: false, errors: {} });
          await fetchCategories();
          setActionLoading(false);
          return;
        }
      }
      
      // If it's a 409 conflict, check if categories were actually created
      if (err.response?.status === 409) {
        // Use caisseIds count as fallback since 409 might mean partial success
        const caisseIdsCount = caisseIdsToAssign?.length || 0;
        if (caisseIdsCount > 0) {
          setSnackbar({ 
            open: true, 
            message: `${caisseIdsCount} catégorie(s) assignée(s) avec succès`, 
            severity: 'success' 
          });
          setAssignCategoryDialog({ open: false, category: null, formData: null, response: null });
          setCreateModal({ open: false, errors: {} });
          await fetchCategories();
          setActionLoading(false);
          return;
        }
      }
      
      // Only show error if we're certain it failed
      setSnackbar({ open: true, message: 'Erreur lors de l\'assignation de la catégorie', severity: 'error' });
      setActionLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditModal({ open: true, category, errors: {} });
  };
  const handleToggleStatus = (category) => {
    const action = category.active ? 'deactivate' : 'activate';
    setStatusDialog({ open: true, category, action });
  };

  const confirmStatusChange = async () => {
    if (!statusDialog.category) return;
    
    try {
      if (statusDialog.action === 'activate') {
        await categoryDetailAPI.activate(statusDialog.category.id);
        setSnackbar({ open: true, message: 'Catégorie activée avec succès', severity: 'success' });
      } else {
        await categoryDetailAPI.deactivate(statusDialog.category.id);
        setSnackbar({ open: true, message: 'Catégorie désactivée avec succès', severity: 'success' });
      }
      
      if (statusDialog.formData) {
        setEditModal({ open: false, category: null, errors: {} });
        await fetchCategories();
      } else {
        const updateCategory = (category) => 
          category.id === statusDialog.category.id 
            ? { ...category, active: statusDialog.action === 'activate' }
            : category;
        
        setCategories(prev => prev.map(updateCategory));
      }
      
      setStatusDialog({ open: false, category: null, action: null, formData: null });
    } catch (err) {
      console.error('Error toggling category status:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification du statut', severity: 'error' });
    }
  };

  const handleToggleNotifications = (category) => {
    const action = category.managerPersonalPreference ? 'deactivate' : 'activate';
    setNotificationDialog({ open: true, category, action });
  };

  const confirmNotificationChange = async () => {
    if (!notificationDialog.category) return;
    
    try {
      await categoryDetailAPI.update(notificationDialog.category.id, {
        notifyManager: notificationDialog.action === 'activate'
      });
      
      const actionText = notificationDialog.action === 'activate' ? 'activées' : 'désactivées';
      setSnackbar({ open: true, message: `Notifications ${actionText} avec succès`, severity: 'success' });
      
      // Update both original and filtered data
      const updateCategory = (category) => 
        category.id === notificationDialog.category.id 
          ? { ...category, managerPersonalPreference: notificationDialog.action === 'activate' }
          : category;
      
      setCategories(prev => prev.map(updateCategory));
      
      setNotificationDialog({ open: false, category: null, action: null });
    } catch (err) {
      console.error('Error toggling notifications:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification des notifications', severity: 'error' });
    }
  };

  const handleTogglePointageClient = async (category) => {
    try {
      const newValue = !category.requiresPointageClient;
      await categoryDetailAPI.update(category.id, { requiresPointageClient: newValue });

      const actionText = newValue ? 'activé' : 'désactivé';
      setSnackbar({ open: true, message: `Pointage Client ${actionText} avec succès`, severity: 'success' });

      const updateCategory = (cat) =>
        cat.id === category.id ? { ...cat, requiresPointageClient: newValue } : cat;
      setCategories(prev => prev.map(updateCategory));
    } catch (err) {
      console.error('Error toggling pointage client:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification du pointage client', severity: 'error' });
    }
  };

  const handleTogglePointageComptabilite = async (category) => {
    try {
      const newValue = !category.requiresPointageComptabilite;
      await categoryDetailAPI.update(category.id, { requiresPointageComptabilite: newValue });

      const actionText = newValue ? 'activé' : 'désactivé';
      setSnackbar({ open: true, message: `Pointage Comptabilité ${actionText} avec succès`, severity: 'success' });

      const updateCategory = (cat) =>
        cat.id === category.id ? { ...cat, requiresPointageComptabilite: newValue } : cat;
      setCategories(prev => prev.map(updateCategory));
    } catch (err) {
      console.error('Error toggling pointage comptabilite:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la modification du pointage comptabilité', severity: 'error' });
    }
  };

  const handleDeleteCategory = (category) => {
    setDeleteDialog({ open: true, category });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.category) return;
    
    try {
      await categoryDetailAPI.delete(deleteDialog.category.id);
      setSnackbar({ open: true, message: 'Catégorie supprimée avec succès', severity: 'success' });
      setDeleteDialog({ open: false, category: null });
      await fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      
      if (err.response?.status === 409) {
        setDeleteDialog({ 
          open: true, 
          category: deleteDialog.category,
          mode: 'disable'
        });
      } else {
        setSnackbar({ open: true, message: 'Erreur lors de la suppression de la catégorie', severity: 'error' });
      }
    }
  };

  const confirmDisable = async () => {
    if (!deleteDialog.category) return;
    
    try {
      await categoryDetailAPI.deactivate(deleteDialog.category.id);
      setSnackbar({ open: true, message: 'Catégorie désactivée avec succès', severity: 'success' });
      setDeleteDialog({ open: false, category: null });
      await fetchCategories();
    } catch (err) {
      console.error('Error disabling category:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la désactivation de la catégorie', severity: 'error' });
    }
  };

  const columns = [
    { 
      key: 'label', 
      label: 'Libellé', 
      width: '21%',
      sortable: true,
      render: (value, row) => (
        <span style={{
          textAlign: 'left',
          font: 'normal normal 600 16px/19px Montserrat',
          letterSpacing: '0px',
          color: '#38485C',
          textTransform: 'none',
          opacity: 0.88
        }}>
          {row.category?.label || row.label}
          {isWithdrawalCategory(row) && (
            <span style={{
              marginLeft: '8px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600
            }}>(Système)</span>
          )}
        </span>
      )
    },
    { 
      key: 'Caisse', 
      label: 'Caisse', 
      width: '17%',
      sortable: true,
      render: (value, row) => (
        <span style={{
          textAlign: 'left',
          font: 'normal normal 600 16px/19px Montserrat',
          letterSpacing: '0px',
          color: '#38485C',
          textTransform: 'none',
          opacity: 0.88
        }}>
          {row.caisse?.name || 'N/A'}
        </span>
      )
    },
    { 
      key: 'caisseFactor', 
      label: 'Effet Caisse', 
      width: '9%',
      sortable: true,
      render: (value, row) => <EffectPill value={parseInt(row.caisseFactor)} />
    },
    { 
      key: 'coffreFactor', 
      label: 'Effet Coffre', 
      width: '9%',
      sortable: true,
      render: (value, row) => <EffectPill value={parseInt(row.coffreFactor)} />
    },
    { 
      key: 'active', 
      label: 'Statut', 
      width: '10%',
      sortable: true,
      render: (value, row) => (
        <div style={{ marginLeft: '-12px' }}>
          <Toggle
            checked={row.active}
            onChange={() => handleToggleStatus(row)}
            label={row.active ? 'Actif' : 'Inactif'}
          />
        </div>
      )
    },
    { 
      key: 'notifyManager', 
      label: 'Notifications', 
      width: '10%',
      sortable: true,
      render: (value, row) => (
        <div style={{ marginLeft: '-12px' }}>
          <Toggle
            checked={row.managerPersonalPreference}
            onChange={() => handleToggleNotifications(row)}
            label={row.managerPersonalPreference ? 'Activé' : 'Désactivé'}
          />
        </div>
      )
    },
    { 
      key: 'requiresPointage', 
      label: 'Pointage', 
      width: '18%',
      sortable: false,
      render: (value, row) => {
        return (
          <div style={{ marginLeft: '-12px' }}>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '4px', 
              alignItems: 'flex-start'
            }}>
              <Toggle
                checked={row.requiresPointageClient || false}
                onChange={() => handleTogglePointageClient(row)}
                label="Client"
              />
              <Toggle
                checked={row.requiresPointageComptabilite || false}
                onChange={() => handleTogglePointageComptabilite(row)}
                label="Comptabilité"
              />
            </Box>
          </div>
        );
      }
    },
    { 
      key: 'actions', 
      label: 'Actions', 
      width: '6%',
      sortable: false,
      render: (value, row) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isWithdrawalCategory(row) && (
            <>
              <CircularIconButton
                icon={editIcon}
                onClick={() => handleEditCategory(row)}
                aria-label="Modifier"
              />
              <CircularIconButton
                icon={trashIcon}
                onClick={() => handleDeleteCategory(row)}
                aria-label="Supprimer"
              />
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Sticky Header Section */}
      <Box
        className="categories-sticky-header"
        sx={{
          position: 'sticky',
          top: `${HEADER_HEIGHT}px`,
          zIndex: 999,
          background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(248, 248, 248, 1), #F8F8F8)'
        }}
      >
        <Box sx={stickyOuterSx}>
          <Box sx={{ paddingTop: '24px', paddingBottom: '8px' }}>
            {/* Page Title & Create Button */}
            <Box sx={{ marginBottom: '8px', width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h1 
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '28px',
                  lineHeight: '32px',
                  letterSpacing: '0px',
                  color: '#38485C',
                  opacity: 0.88,
                  textTransform: 'none',
                  margin: 0
                }}
              >
                Categories
              </h1>

              {user?.role === 'MANAGER' && (
                <Button
                  variant="contained"
                  startIcon={<img src={plusIcon} alt="plus" style={{ width: '16px', height: '16px' }} />}
                  onClick={handleCreateCategory}
                  sx={{
                    height: '40px',
                    backgroundColor: '#027BC6',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    opacity: 1,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#025a9c'
                    }
                  }}
                >
                  Créer une nouvelle catégorie
                </Button>
              )}
            </Box>

            {/* Filters */}
            <Box sx={{ width: '100%', overflow: 'visible', position: 'relative', zIndex: 1001 }}>
              <CategoryFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onReset={handleResetFilters}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={contentContainerSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              paddingTop: '16px',
              paddingBottom: '8px',
              paddingLeft: '16px',
              paddingRight: '16px',
              color: '#919191',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat'
            }}
          >
            {columns.map((column, index) => {
              const isActionsColumn = column.key === 'actions';
              const isSortable = column.sortable && handleSort;
              // Map column key to API sort field for comparison
              const apiSortField = column.key === 'label' ? 'category.label' : column.key;
              const isCurrentlySorted = sortBy === apiSortField;
              
              return (
                <Box
                  key={index}
                  sx={{
                    paddingLeft: '24px',
                    paddingRight: '24px',
                    textAlign: isActionsColumn ? 'center' : 'left',
                    ...(isActionsColumn 
                      ? { display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }
                      : column.width
                        ? { flex: 'none', width: column.width }
                        : { flex: 1, minWidth: '150px' }),
                    ...(isActionsColumn && column.width ? { width: column.width, flexShrink: 0, flexGrow: 0 } : {}),
                    ...(column.width && !isActionsColumn ? { width: column.width } : {})
                  }}
                >
                  {isSortable ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        cursor: 'pointer',
                        '&:hover': { color: '#027BC6' },
                        transition: 'color 0.2s',
                        color: isCurrentlySorted ? '#027BC6' : '#919191'
                      }}
                      onClick={() => handleSort(column.key, sortBy === column.key && sortDirection === 'ASC' ? 'DESC' : 'ASC')}
                    >
                      <span>{column.label}</span>
                      {isCurrentlySorted && (
                        <img
                          src={sortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon}
                          alt={sortDirection === 'ASC' ? 'ascending' : 'descending'}
                          style={{
                            width: '12px',
                            height: '12px',
                            filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    column.label
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Categories List */}
      <Box sx={{ ...contentContainerSx, paddingBottom: '32px' }}>
        <Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Rows
              data={getPaginatedData()}
              columns={columns}
              pagination={{
                page: pagination.page,
                size: pagination.size,
                total: pagination.total,
                onPageChange: (newPage) => setPagination(prev => ({ ...prev, page: newPage })),
                onSizeChange: (newSize) => setPagination(prev => ({ ...prev, size: newSize, page: 0 }))
              }}
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSort={handleSort}
              emptyMessage="Aucune catégorie trouvée"
              hideHeader={true}
              containerPadding={0}
            />
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, category: null })}
        onConfirm={deleteDialog.mode === 'disable' ? confirmDisable : confirmDelete}
        type={deleteDialog.mode === 'disable' ? 'deactivate' : 'delete'}
        title={deleteDialog.mode === 'disable' ? 'Désactiver la catégorie' : 'Supprimer la catégorie'}
        messageComponent={deleteDialog.mode === 'disable' ? (
          <span>
            Cette catégorie a au moins une transaction. Elle sera{' '}
            <span style={{ color: '#007BC6' }}>désactivée</span> au lieu d'être supprimée.
            <br /><br />
            Êtes-vous sûr de vouloir désactiver la catégorie{' '}
            <span style={{ color: '#007BC6' }}>{deleteDialog.category?.label}</span>
            {' '}?
          </span>
        ) : undefined}
        message={deleteDialog.mode === 'disable' ? undefined : (
          <>
            Êtes-vous sûr de vouloir supprimer la catégorie{' '}
            <span style={{ color: '#007BC6' }}>{deleteDialog.category?.label}</span>
            {' '}? Cette action est irréversible.
          </>
        )}
        confirmText={deleteDialog.mode === 'disable' ? 'Désactiver' : 'Supprimer'}
        cancelText="Annuler"
        loading={false}
        hasExtendedContent={deleteDialog.mode === 'disable'}
      />

      {/* Edit Confirmation Dialog */}
      <ConfirmationDialog
        open={editConfirmDialog.open}
        onClose={() => setEditConfirmDialog({ open: false, changes: null })}
        onConfirm={confirmEditChanges}
        type="confirm"
        title="Confirmer les modifications"
        message="Êtes-vous sûr de vouloir enregistrer ces modifications ?"
        loading={actionLoading}
      />

      {/* Status Change Confirmation Dialog */}
      <ConfirmationDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog({ open: false, category: null, action: null, formData: null })}
        onConfirm={confirmStatusChange}
        type={statusDialog.action === 'activate' ? 'activate' : 'deactivate'}
        title={statusDialog.action === 'activate' ? 'Activer la catégorie' : 'Désactiver la catégorie'}
        messageComponent={
          <span>
            Êtes-vous sûr de vouloir{' '}
            {statusDialog.action === 'activate' ? 'activer' : 'désactiver'} la catégorie{' '}
            <span style={{ color: '#027BC6' }}>{statusDialog.category?.label}</span>
            {' '}?
          </span>
        }
        confirmText={statusDialog.action === 'activate' ? 'Activer' : 'Désactiver'}
        cancelText="Annuler"
        loading={false}
      />

      {/* Notification Change Confirmation Dialog */}
      <ConfirmationDialog
        open={notificationDialog.open}
        onClose={() => setNotificationDialog({ open: false, category: null, action: null })}
        onConfirm={confirmNotificationChange}
        type={notificationDialog.action === 'activate' ? 'activate' : 'deactivate'}
        title={notificationDialog.action === 'activate' ? 'Activer les notifications' : 'Désactiver les notifications'}
        messageComponent={
          <span>
            Êtes-vous sûr de vouloir{' '}
            {notificationDialog.action === 'activate' ? 'activer les notifications' : 'désactiver les notifications'} pour{' '}
            <span style={{ color: '#027BC6' }}>{notificationDialog.category?.label}</span>
            {' '}?
          </span>
        }
        confirmText={notificationDialog.action === 'activate' ? 'Activer' : 'Désactiver'}
        cancelText="Annuler"
        loading={false}
      />


      {/* Create Category Modal */}
      <CreationModal
        open={createModal.open}
        onClose={() => setCreateModal({ open: false, errors: {} })}
        onSubmit={handleSubmitCreateCategory}
        title="Créer une nouvelle catégorie"
        fields={getCategoryFields}
        fieldsPerRow={3}
        submitButtonText="Créer une nouvelle catégorie"
        loading={actionLoading}
        toggleConfig={{
          field: 'active',
          activeLabel: 'Actif',
          inactiveLabel: 'Inactif'
        }}
        externalErrors={createModal.errors}
        initialData={useMemo(() => ({ 
          label: '', 
          caisseFactor: '', 
          coffreFactor: '', 
          caisseIds: [], 
          notifyManager: '', 
          requiresPointageClient: '', 
          requiresPointageComptabilite: '', 
          active: true 
        }), [])}
      />

      {/* Edit Category Modal */}
      <CreationModal
        open={editModal.open}
        onClose={() => setEditModal({ open: false, category: null, errors: {} })}
        onSubmit={handleSubmitEditCategory}
        title="Modifier la catégorie"
        fields={editCategoryFields}
        submitButtonText="Enregistrer"
        loading={actionLoading}
        toggleConfig={{
          field: 'active',
          activeLabel: 'Actif',
          inactiveLabel: 'Inactif'
        }}
        showLabels={true}
        initialData={useMemo(() => editModal.category ? {
          label: editModal.category.category?.label || editModal.category.label || '',
          caisseFactor: editModal.category.caisseFactor !== undefined ? editModal.category.caisseFactor : '',
          coffreFactor: editModal.category.coffreFactor !== undefined ? editModal.category.coffreFactor : '',
          notifyManager: editModal.category.managerPersonalPreference !== undefined ? editModal.category.managerPersonalPreference : false,
          requiresPointageClient: editModal.category.requiresPointageClient !== undefined ? editModal.category.requiresPointageClient : false,
          requiresPointageComptabilite: editModal.category.requiresPointageComptabilite !== undefined ? editModal.category.requiresPointageComptabilite : false,
          active: editModal.category.active !== undefined ? editModal.category.active : true
        } : {}, [editModal.category])}
      />

      {/* Assign Category Confirmation Dialog */}
      <ConfirmationDialog
        open={assignCategoryDialog.open}
        onClose={() => setAssignCategoryDialog({ open: false, category: null, formData: null, response: null })}
        onConfirm={handleConfirmAssignCategory}
        type="confirm"
        title="Assigner la catégorie"
        messageComponent={
          assignCategoryDialog.response ? (() => {
            const response = assignCategoryDialog.response;
            const caisseNames = response.selectedCaissesWithCategory?.map(c => c.name).join(", ") || "";
            
            if (response.selectedCaissesWithCategory && response.selectedCaissesWithCategory.length > 0) {
              if (response.selectedCaissesWithoutCategory && response.selectedCaissesWithoutCategory.length > 0) {
                return (
                  <span>
                    La catégorie existe déjà pour la caisse{' '}
                    <span style={{ color: '#027BC6' }}>{caisseNames}</span>
                    . Voulez-vous l'assigner aux autres caisses sélectionnées ?
                  </span>
                );
              } else {
                return (
                  <span>
                    La catégorie existe déjà pour la caisse{' '}
                    <span style={{ color: '#027BC6' }}>{caisseNames}</span>
                  </span>
                );
              }
            } else {
              const existingCaisseNames = response.existingCaisses?.map(c => c.name).join(", ") || "";
              return (
                <span>
                  La catégorie existe déjà pour la caisse{' '}
                  <span style={{ color: '#027BC6' }}>{existingCaisseNames}</span>
                  . Voulez-vous l'assigner à vos caisses sélectionnées ?
                </span>
              );
            }
          })() : null
        }
        confirmText="Assigner"
        cancelText="Annuler"
        loading={actionLoading}
      />

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </Box>
  );
};

export default Categories;